import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, ElementRef, AfterViewInit, OnDestroy, ChangeDetectorRef, OnInit } from '@angular/core';
import { CarouselSliderComponent } from '../../../../shared/components/slider/carousel-slider';
import { LoanOfferCardComponent, LoanOffer } from './loan-offer-card/loan-offer-card';
import { RegistrationModalComponent } from './registration-modal/registration-modal';
import { PartnerService } from '../../../../core/services/partner.service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-loan-offers-widget',
  standalone: true,
  imports: [CommonModule, CarouselSliderComponent, LoanOfferCardComponent, RegistrationModalComponent],
  templateUrl: './loan-offers-widget.html',
  styleUrl: './loan-offers-widget.scss',
})
export class LoanOffersWidgetComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() title: string = 'Loan offers';
  @Input() offers: LoanOffer[] = [];
  @Input() isEditMode: boolean = false;
  @Input() isAddMode: boolean = false;
  @Output() addWidget = new EventEmitter<void>();
  @Input() index = 0;
  @Input() activeDropdownIndex: number | null = null;
  @Input() getAvailableSizes!: (index: number) => Array<{ value: string; label: string }>;
  @Input() toggleSizeDropdown!: (index: number, event: Event) => void;
  @Input() changeWidgetSize!: (index: number, size: any) => void;
  @Input() removeWidget!: (index: number) => void;
  @Input() size: 'small' | 'medium' | 'half' | 'full' = 'small';

  @Output() selectOffer = new EventEmitter<LoanOffer>();

  isLoading: boolean = false;
  errorMessage: string | null = null;
  leadId: string | null = null;
  showRegistrationModal: boolean = false;
  userMobile: string = '';

  private resizeObserver: ResizeObserver | null = null;
  dynamicMaxHeight: number | null = null;

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private partnerService: PartnerService
  ) { }

  ngOnInit(): void {
    const mobile = localStorage.getItem('consent_mobile_number');
    const storedLeadId = localStorage.getItem('partner_lead_id');

    this.userMobile = mobile || '';

    if (storedLeadId) {
      this.leadId = storedLeadId;
      this.getOffers(storedLeadId);
    } else if (mobile) {
      this.fetchLoanOffers(mobile);
    } else {
      this.errorMessage = 'Mobile number not found. Please link your account.';
    }
  }

  fetchLoanOffers(mobile: string): void {
    this.isLoading = true;
    this.errorMessage = null;

    // 1. Check eligibility (Dedupe)
    this.partnerService.checkEligibility(mobile).subscribe({
      next: (response: any) => {
        const isSuccess = response && (response.status === 'success' || response.success === 'true' || response.success === true);

        // Extract leadId from root or data object
        const foundLeadId = response.leadId || (response.data && response.data.leadId);

        if (isSuccess && foundLeadId) {
          this.leadId = foundLeadId;
          localStorage.setItem('partner_lead_id', this.leadId!);
          this.getOffers(this.leadId!);
        } else if (isSuccess && response.message === 'Eligible') {
          this.isLoading = false;
          this.errorMessage = 'You are eligible for loan offers! Please register to see your personalized list.';
          this.cdr.detectChanges();
        } else {
          this.isLoading = false;
          this.errorMessage = response.message || 'No existing lead found. Please register to see offers.';
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage = 'Failed to check eligibility.';
        console.error('Eligibility Error:', err);
        this.cdr.detectChanges();
      }
    });
  }

  private getOffers(leadId: string): void {
    this.partnerService.getOffers(leadId)
      .pipe(finalize(() => {
        this.isLoading = false;
        this.cdr.detectChanges();
      }))
      .subscribe({
        next: (response: any) => {
          const isSuccess = response && (response.status === 'success' || response.success === 'true' || response.success === true);
          const offersData = Array.isArray(response) ? response : (response.data || response.offers || []);

          if (isSuccess && Array.isArray(offersData) && offersData.length > 0) {
            this.offers = offersData.map((offer: any) => {
              // Extract APR and handle units
              let apr = 'N/A';
              const rawApr = offer.offerInterestRate || offer.apr || offer.interest_rate;
              if (rawApr) {
                apr = String(rawApr);
                if (!apr.includes('%')) {
                  apr += '%';
                }
              }

              // Extract Loan Term and handle units
              let loanTerm = 'N/A';
              const rawTerm = offer.offerTenure || offer.loanTerm || offer.tenure || offer.term;
              if (rawTerm) {
                loanTerm = String(rawTerm);
                if (!loanTerm.toLowerCase().includes('month')) {
                  loanTerm += ' Months';
                }
              }

              return {
                loanProviderImage: offer.lenderLogo || offer.loanProviderImage || offer.bank_logo || 'assets/images/fairstone-financial-inc-upsizes-credit.jpg',
                loanAmount: `₹${this.formatCurrency(offer.offerAmountUpTo || offer.loanAmount || offer.min_loan_amount || offer.amount)}`,
                apr: apr,
                loanTerm: loanTerm,
                monthlyPayment: offer.monthlyPayment ? `₹${this.formatCurrency(offer.monthlyPayment)}` : (offer.emi ? `₹${this.formatCurrency(offer.emi)}` : 'N/A'),
                offerLink: offer.offerLink
              };
            });
          } else {
            this.errorMessage = 'No loan offers available at this time.';
          }
        },
        error: (err) => {
          this.errorMessage = 'Failed to fetch loan offers.';
          console.error('Fetch Offers Error:', err);
        }
      });
  }

  private formatCurrency(value: number | string | undefined | null): string {
    if (value === undefined || value === null || value === 'N/A' || value === '') return '0';
    let numStr = String(value).replace(/[^0-9.]/g, ''); // Improved to remove any non-numeric chars except dot
    const num = parseFloat(numStr);
    return isNaN(num) ? '0' : new Intl.NumberFormat('en-IN').format(num);
  }

  openRegistration(): void {
    this.showRegistrationModal = true;
  }

  closeRegistration(): void {
    this.showRegistrationModal = false;
  }

  onRegistrationSuccess(leadId: string): void {
    this.showRegistrationModal = false;
    this.leadId = leadId;

    if (leadId) {
      this.isLoading = true;
      this.errorMessage = null;
      this.getOffers(leadId);
    } else {
      this.fetchLoanOffers(this.userMobile);
    }
  }

  ngAfterViewInit() {
    this.setupResizeObserver();
  }

  private setupResizeObserver() {
    const parentElement = this.elementRef.nativeElement.parentElement;
    if (parentElement) {
      this.resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
          if (!this.resizeObserver) return;

          const widgetEl = parentElement.closest('.widget') as HTMLElement;
          const hasExplicitHeight = widgetEl && widgetEl.style.height && widgetEl.style.height !== 'auto' && widgetEl.style.height !== '';

          let targetHeight = parentElement.offsetHeight;

          if (!hasExplicitHeight) {
            targetHeight = 260; // Forcefully map to exactly span 56 initially
          }

          this.dynamicMaxHeight = targetHeight;
          this.cdr.detectChanges();
        });
      });
      this.resizeObserver.observe(parentElement);
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  onOfferSelected(offer: LoanOffer): void {
    if (this.isAddMode || this.isEditMode) return;

    if (offer.offerLink) {
      window.open(offer.offerLink, '_blank');
    }

    this.selectOffer.emit(offer);
  }
}
